/**
 * Estimate Approval Service
 *
 * Wraps the estimate-approval endpoints on Work Orders. Approval
 * comes in two flavors, distinguished by the `approverType` field in
 * the request body:
 *
 *   - 'super_admin' : the authenticated user is approving as a Super
 *                     Admin. Server enforces that they actually hold
 *                     the role.
 *   - 'contact'     : the customer-side contact at the business is
 *                     approving in person. The body must include a
 *                     contact_id that belongs to the parent record's
 *                     company. Used in the same model as the existing
 *                     signature-capture flow — a manager hands the
 *                     device to the customer at the job site.
 *
 * The estimate fields themselves (estimated_cost, estimate_min,
 * estimate_max) live on the work order and are edited via the regular
 * WO update endpoint; only the approval state has its own surface.
 * Inspections are not priced and have no approval surface at all.
 *
 * Backend endpoints:
 *   POST   /api/v1/work-orders/{id}/estimate-approval
 *   DELETE /api/v1/work-orders/{id}/estimate-approval
 */

import { api } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * The two valid approver types. The server uses this to decide which
 * approver FK to populate (estimate_approved_by_user_id vs
 * estimate_approved_by_contact_id).
 */
export type EstimateApproverType = 'super_admin' | 'contact';

/**
 * Body shape posted when approving an estimate. `contactId` is
 * required when approverType is 'contact'; ignored otherwise.
 */
export interface ApproveEstimatePayload {
    approverType: EstimateApproverType;
    contactId?: number;
}

/* ============================================
 * SERVICE
 * ============================================ */

export const estimateApprovalService = {
    /** POST /work-orders/{id}/estimate-approval */
    approveWorkOrder: (workOrderId: string, payload: ApproveEstimatePayload) =>
        api.post(`/work-orders/${workOrderId}/estimate-approval`, payload),

    /** DELETE /work-orders/{id}/estimate-approval */
    revokeWorkOrder: (workOrderId: string) =>
        api.delete(`/work-orders/${workOrderId}/estimate-approval`),

};
